import React, { useCallback, useEffect } from 'react';
import { ModalBody, ConfirmationPageModal, HoverAnimationBtn } from './styles';
import { useTranslation } from 'react-i18next';
import {
  useDocumentContext,
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
} from '../../context/complexDocumentContext';
import { getElementBySelectorAsync } from '../../containers/ComplexDocumentDesign/complexDocumentHelper';
import { deepCopy, scrollPageTo } from '../../utils/javascript';
import {
  COMPLEX_DOCUMENT_PAGE_WRAPPER,
  CREATIVE_BLOCK,
  DESIGN_TYPE,
  DYNAMIC_TABLE_BLOCK,
  FOOTER,
  HEADER,
  STANDALONE_BLOCK,
  TEXT_BLOCK,
} from '../../utils/complexDocumentConstants';
import { EMAIL_DESIGN_TYPE } from '../../utils/complexEmailConstants';
import { getStandaloneFlow } from '../../utils/services/complexDocumentsAPI';
import { message } from 'antd';
import { CONFIRMATION_PAGE_MODAL } from './translationValues';
import { withTranslation } from 'react-i18next';
import { useArtboardContext, CONTEXT_KEYS } from '../../context/complexArtboardContext';
import { REUSABLE_FLOW, REUSABLE_SECTION, STATIC_TABLE_ELEMENT } from '../../utils/complexDocumentConstants';
import { autoTransform, checkSessionUserAccess } from '../../utils/helper';
import { detectLanguageDynamic } from '../../utils/services/textGenerationAPI';
import { useSessionContext } from '../../context/sessionContext';

const NAMESPACES = ['CONFIRMATION_PAGE_MODAL'];
const ConfirmationPageDialog = ({isEmailTemplate = false}) => {
  const { t } = useTranslation(NAMESPACES);

  const reusableTypeSelector = useCallback(store => store[CONTEXT_KEYS.REUSABLE_TYPE], []);
  const [reusableType, updateArtboard] = useArtboardContext(reusableTypeSelector, 'ConfirmationPageModal');

  const storeSelector = useCallback(store => deepCopy(store), []);
  const [store] = useDocumentContext(storeSelector, 'ConfirmationPageModal');

  const customSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_FLOW], []);
  const [cloneTemplate, methods] = useDocumentContext(customSelector, 'ConfirmationPageModal');

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexArtboard`);

  let visible = (cloneTemplate && !isInteractiveDesign) ? Object.keys(cloneTemplate).length > 0 : false;

  const blockSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_BLOCK], []);
  const [blockTemplate] = useDocumentContext(blockSelector, 'ConfirmationPageModal');

  visible = visible || (blockTemplate && !isInteractiveDesign ? Object.keys(blockTemplate).length > 0 : false);

  const currentDesignSelector = useCallback((store) => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const getFlowType = async type => {
    const isStandalone = type === 'refrenced-flow' ? true : false;
    const templateResponse = await getStandaloneFlow(cloneTemplate.flowId, false);

    if (templateResponse?.statusCode === 2605) {
      methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_CLONED]: true });
      const flowItem = templateResponse.data.flow;
      const newFlowUUID = methods.setNewFlow(
        flowItem,
        cloneTemplate.uuid,
        cloneTemplate.flowNumber,
        isStandalone,
        currentDesign
      );
      getElementBySelectorAsync(`#scroll-${newFlowUUID}-0`).then(target => {
        scrollPageTo(target, COMPLEX_DOCUMENT_PAGE_WRAPPER);
      });
    } else {
      methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_FLOW]: undefined });
      message.warn(
        t(
          CONFIRMATION_PAGE_MODAL.WARNING_MESSAGE.key,
          CONFIRMATION_PAGE_MODAL.WARNING_MESSAGE.value,
        ),
      );
    }
  };

  const resetCloneInfo = () => {
    methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_BLOCK]: undefined });
    updateArtboard({ [CONTEXT_KEYS.REUSABLE_TYPE]: '' });
  };
  
  const getBlocks = type => {
    const isStandalone = type === 'refrenced-block' ? true : false;
    let blocks = deepCopy(blockTemplate.blocks);
    const sectionType = blockTemplate.sectionType;

    if (isInteractiveDesign) {
      if ([HEADER, FOOTER].includes(sectionType)) {
        message.error(
          t(
            CONFIRMATION_PAGE_MODAL.HEADER_FOOTER_NOT_ALLOWED.key,
            CONFIRMATION_PAGE_MODAL.HEADER_FOOTER_NOT_ALLOWED.value,
          ),
        );
        resetCloneInfo();
        return;
      }

      if (sectionType === 'content') {
        const allowedTypes = new Set([CREATIVE_BLOCK, TEXT_BLOCK]);

        const isValidBlocks = blocks.every(block => allowedTypes.has(block.blockType));
        if (!isValidBlocks) {
          message.error(
            t(
              CONFIRMATION_PAGE_MODAL.ONLY_TEXT_CREATIVE_BLOCK.key,
              CONFIRMATION_PAGE_MODAL.ONLY_TEXT_CREATIVE_BLOCK.value,
            ),
          );
          resetCloneInfo();
          return;
        }

        const hasNoVariables = blocks.every(block => block.variableContract.length === 0);
        if (!hasNoVariables) {
          message.error(
            t(
              CONFIRMATION_PAGE_MODAL.SECTIONS_WITHOUT_VARIABLES.key,
              CONFIRMATION_PAGE_MODAL.SECTIONS_WITHOUT_VARIABLES.value,
            ),
          );
          resetCloneInfo();
          return;
        }
      }
    }

    blocks = blocks.map(obj => {
      // we are changing isEditable to true for interactive design
      const newObj = {...obj, isEditable: isInteractiveDesign && [TEXT_BLOCK, CREATIVE_BLOCK].includes(obj.blockType) ? true : (obj.isEditable || false)};
      if(Array.isArray(newObj.contents)){
        newObj.contents.forEach(content => {
         delete content.uuid;
         if (content.contentType === STATIC_TABLE_ELEMENT) {
           content.contents = content.contents.map(cell => {
             delete cell.uuid;
             return cell;
           });
         }
        });
      }
      if(newObj.blockType === DYNAMIC_TABLE_BLOCK){
        newObj.blockDTOList.forEach(list=>{
          list.contents.forEach(content => {
            delete content.uuid;
           });
           list.type = list.blockType;
           list.isStandalone = false;
        })
      }
      newObj.type = newObj.blockType;
      delete newObj.orderNumber;
      delete newObj.isStandalone;
      !isStandalone && delete newObj.blockId;
      return newObj;
    });
    const flow = store[blockTemplate.uuid];
    const headerHeight = flow.header ? store[flow?.header].style?.height || 0 : 0;
    const footerHeight = flow.footer ? store[flow?.footer].style?.height || 0 : 0;
    const  pageDetails = {totalVerticalMargin: flow.margin[0]+flow.margin[2], totalHeaderFooterHeight: headerHeight + footerHeight }
    const parentBlock = store[blockTemplate.uuid];
    const { updatedBlocks } = autoTransform(
      blocks,
      isEmailTemplate ? { width: parentBlock.width, height: 1 } : parentBlock,
      isEmailTemplate ? 590 : undefined,
      isEmailTemplate ? 1 : undefined,
      pageDetails,
    );
    if ([HEADER, FOOTER].includes(sectionType)) {
      isStandalone &&
        methods.setPage('addStaticElement', {
          uuid: blockTemplate.uuid,
          type: blockTemplate.type,
          block: Object.assign({}, updatedBlocks[0], {
            isStandalone: isStandalone,
            blockId: blockTemplate.blockId,
            style: {...updatedBlocks[0].style, ...blockTemplate.style},
          }),
          
        });
      if (!isStandalone) {
        methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_CLONED]: true });
        methods.setPage('addStaticElement', {
          uuid: blockTemplate.uuid,
          type: blockTemplate.type,
          block: Object.assign({}, updatedBlocks[0], { isStandalone: isStandalone, style: {...updatedBlocks[0].style, ...blockTemplate.style} }),
        });
      }
    } else {
      if (!isStandalone) {
        methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_CLONED]: true });
        methods.setPage('addMultipleBlocks', {
          uuid: blockTemplate.uuid,
          blocks: updatedBlocks,
          pageId: blockTemplate.pageId,
          dropType: blockTemplate.dropType,
          dropItem: blockTemplate.dropItem,
          currentPageId: blockTemplate.currentPageId,
          style: blockTemplate.style,
          isEmailTemplate: isEmailTemplate
        });
      }
      if (isStandalone) {
        const parentBlock = Object.assign({}, blockTemplate, {
          block: {
            blockId: blockTemplate.blockId,
            blockType: STANDALONE_BLOCK,
            type: STANDALONE_BLOCK,
            blockDTOList: updatedBlocks,
            isStandalone: isStandalone,
            variableContract: blockTemplate.variableContract,
            style: blockTemplate.style,
          },
        });
        delete parentBlock.blocks;
        methods.setPage('addBlock', parentBlock);
      }
    }
    methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_BLOCK]: undefined });
    updateArtboard({[CONTEXT_KEYS.REUSABLE_TYPE]: ''});
    const designType = isEmailTemplate ? EMAIL_DESIGN_TYPE.EMAIL_BLOCK : DESIGN_TYPE.BLOCK;
  };

  const handleClick = type => {
    const actionMap = {
      refrenced: {
        [REUSABLE_FLOW]: () => getFlowType('refrenced-flow'),
        [REUSABLE_SECTION]: () => getBlocks('refrenced-block'),
      },
      reusable: {
        [REUSABLE_FLOW]: () => getFlowType('reusable-flow'),
        [REUSABLE_SECTION]: () => getBlocks('reusable-block'),
      },
    };

    const action = actionMap[type][reusableType];
    if (action) {
      action();
      updateArtboard({[CONTEXT_KEYS.REUSABLE_TYPE]: ''});
    }
  };

  const onCancel = () => {
    methods.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_BLOCK]: undefined, [COMPLEX_DOCUMENT_CONTEXT_KEYS.CLONE_FLOW]: undefined });
  };

  const title =
    reusableType === REUSABLE_FLOW
      ? t(
          CONFIRMATION_PAGE_MODAL.PAGE_MODAL_TITLE.key,
          CONFIRMATION_PAGE_MODAL.PAGE_MODAL_TITLE.value,
        )
      : reusableType === REUSABLE_SECTION
      ? t(
          CONFIRMATION_PAGE_MODAL.SECTION_MODAL_TITLE.key,
          CONFIRMATION_PAGE_MODAL.SECTION_MODAL_TITLE.value,
        )
      : '';

  const attachDescription =
    reusableType === REUSABLE_FLOW
      ? t(
          CONFIRMATION_PAGE_MODAL.ATTACH_PAGE_DESCRIPTION.key,
          CONFIRMATION_PAGE_MODAL.ATTACH_PAGE_DESCRIPTION.value,
        )
      : reusableType === REUSABLE_SECTION
      ? t(
          CONFIRMATION_PAGE_MODAL.ATTACH_SECTION_DESCRIPTION.key,
          CONFIRMATION_PAGE_MODAL.ATTACH_SECTION_DESCRIPTION.value,
        )
      : '';

  const cloneDescription =
    reusableType === REUSABLE_FLOW
      ? t(
          CONFIRMATION_PAGE_MODAL.CLONE_PAGE_DESCRIPTION.key,
          CONFIRMATION_PAGE_MODAL.CLONE_PAGE_DESCRIPTION.value,
        )
      : reusableType === REUSABLE_SECTION
      ? t(
          CONFIRMATION_PAGE_MODAL.CLONE_SECTION_DESCRIPTION.key,
          CONFIRMATION_PAGE_MODAL.CLONE_SECTION_DESCRIPTION.value,
        )
      : '';

  const cloneButton =
    reusableType === REUSABLE_FLOW
      ? t(
          CONFIRMATION_PAGE_MODAL.CLONE_PAGES_BUTTON.key,
          CONFIRMATION_PAGE_MODAL.CLONE_PAGES_BUTTON.value,
        )
      : reusableType === REUSABLE_SECTION
      ? t(
          CONFIRMATION_PAGE_MODAL.CLONE_SECTION_BUTTON.key,
          CONFIRMATION_PAGE_MODAL.CLONE_SECTION_BUTTON.value,
        )
      : '';

  useEffect(() => {
    if(reusableType === REUSABLE_SECTION && isInteractiveDesign){
      getBlocks('reusable-block')
    }
  }, [reusableType]);

  if (!visible) return <div />; 

  return (
    <ConfirmationPageModal
      title={title}
      centered
      visible={visible}
      footer={null}
      width={705}
      style={{ width: '745px' }}
      ConfirmationPageDialog={true}
      onCancel={onCancel}
      maskClosable={false}
      className="ConfirmationPageModal"
      maskstyle={{ backgroundcolor: 'rgba(0,0,0,0.01)' }}
      transparentCard={false}
    >
      <ModalBody>
        <div className="ConfirmationPageDialog">
          <div className="content-wrapper">
            <div className="txt">{attachDescription}</div>
            <div className="custom-btn">
              <HoverAnimationBtn height="55px" onClick={() => handleClick('refrenced')}>
                <button
                  data-hover={t(
                    CONFIRMATION_PAGE_MODAL.ATTACH_BUTTON.key,
                    CONFIRMATION_PAGE_MODAL.ATTACH_BUTTON.value,
                  )}
                >
                  <div>
                    {t(
                      CONFIRMATION_PAGE_MODAL.ATTACH_BUTTON.key,
                      CONFIRMATION_PAGE_MODAL.ATTACH_BUTTON.value,
                    )}
                  </div>
                </button>
              </HoverAnimationBtn>
            </div>
          </div>

          <div className="content-wrapper" style={{ marginTop: '20px' }}>
            <div className="txt">{cloneDescription}</div>
            <div className="custom-btn">
              <HoverAnimationBtn height="55px" onClick={() => handleClick('reusable')}>
                <button data-hover={cloneButton}>
                  <div>{cloneButton}</div>
                </button>
              </HoverAnimationBtn>
            </div>
          </div>
        </div>
      </ModalBody>
    </ConfirmationPageModal>
  );
};
export default withTranslation(NAMESPACES)(ConfirmationPageDialog);
